import { App } from 'App';
import { Button, Card, Divider, Heading, Spacer } from 'internalLibrary';
import { formatCurrencyOrMock } from 'formatters/formatCurrencyOrMock';
import { formatNumberToLocaleOrMock } from 'formatters/formatNumberToLocaleOrMock';
import { useAnalytics } from 'use-analytics';
import { useCalculator } from 'ui/components/CalculatorProvider';
import { useCallback } from 'react';
import Slider from 'ui/components/Slider/Slider';
import styles from './WelcomeBack.module.scss';

interface Props {
  onButtonClick: () => void;
  buttonDisabled: boolean;
}

function WelcomeBackCalculator({ onButtonClick, buttonDisabled }: Props) {
  const {
    min,
    max,
    step,
    shouldDisplayLoading,
    amount,
    onAmountChange,
    offer,
  } = useCalculator();
  const { track } = useAnalytics();

  const t = App.translateContent('leftPanel');
  const tCommon = App.translateContent('common');

  const onAfterChange = useCallback((value: number) => {
    track('calculator', {
      description: 'amount',
      value,
    });
  }, []);

  return (
    <Card className={styles.card}>
      <Heading text={t.welcomeBackTitle} />
      {max !== undefined && min !== undefined && (
        <div className={styles.calculator}>
          <Slider
            className={styles['slider-element']}
            disabled={shouldDisplayLoading}
            loading={shouldDisplayLoading}
            max={max}
            min={min}
            step={step}
            onChange={onAmountChange}
            units={tCommon.currencySymbol}
            value={amount}
            onAfterChange={onAfterChange}
          />
        </div>
      )}
      <Spacer marginVertical={'20px'}>
        <div className={styles['column-info']}>
          <div className={styles['value-large']}>
            {' '}
            {shouldDisplayLoading ? `##-##-##` : offer?.agreementEndDate}
          </div>
          <div className={styles.description}>{t.daysToPayment}</div>
        </div>
      </Spacer>

      <div className={styles.grid}>
        <div className={styles['column-info']}>
          <div className={styles.description}>{t.interest}</div>
          <div className={styles.value}>
            {formatCurrencyOrMock(
              offer?.firstPaymentInterest,
              '###,#',
              shouldDisplayLoading,
            )}
          </div>
        </div>

        <div className={styles['column-info']}>
          <div className={styles.description}>{t.setupFee}</div>
          <div className={styles.value}>
            {formatCurrencyOrMock(
              offer?.additionalAmountAdministrationFee,
              '###',
              shouldDisplayLoading,
            )}
          </div>
        </div>

        <div className={styles['column-info']}>
          <div className={styles.description}>{t.effectiveInterestRate}</div>
          <div className={styles.value}>
            {formatNumberToLocaleOrMock(
              offer?.annualPercentageRate,
              '##,#',
              shouldDisplayLoading,
            )}{' '}
            {tCommon.percentSymbol}
          </div>
        </div>

        <div className={styles['column-info']}>
          <div className={styles.description}>{t.toPay}</div>
          <div className={styles.value}>
            {formatCurrencyOrMock(
              offer?.totalRepayableAmount,
              '## ###,#',
              shouldDisplayLoading,
            )}
          </div>
        </div>

        <div className={styles['column-info']}>
          <div className={styles.description}>{t.leastAmountToPay}</div>
          <div className={styles.value}>
            {formatCurrencyOrMock(
              offer?.mtpAmount,
              '# ###,##',
              shouldDisplayLoading,
            )}
          </div>
        </div>
      </div>

      <Spacer marginVertical={'20px'}>
        <Divider />
      </Spacer>

      <div className={styles.grid}>
        <div className={styles['row-info']}>
          <div className={styles.description}>{t.amortization}</div>
          <div className={styles.value}>
            {' '}
            {formatCurrencyOrMock(
              offer?.mtpLoanAmortizationAmount,
              '###,##',
              shouldDisplayLoading,
            )}
          </div>
        </div>
        <div className={styles['row-info']}>
          <div className={styles.description}>{t.interest}</div>
          <div className={styles.value}>
            {formatCurrencyOrMock(
              offer?.mtpInterestAmount,
              '###,##',
              shouldDisplayLoading,
            )}
          </div>
        </div>
        <div className={styles['row-info']}>
          <div className={styles.description}>
            {t.accountingFeeForPartialPayment}
          </div>
          <div className={styles.value}>
            {formatCurrencyOrMock(
              offer?.mtpAdministrationFee,
              '#',
              shouldDisplayLoading,
            )}
          </div>
        </div>

        <div className={styles['row-info']}>
          <div className={styles.description}>{t.invoiceFee}</div>
          <div className={styles.value}>
            {formatCurrencyOrMock(
              offer?.mtpInvoicingFee,
              '#',
              shouldDisplayLoading,
            )}
          </div>
        </div>

        <div className={styles['row-info']}>
          <div className={styles.description}>{t.setupFee}</div>
          <div className={styles.value}>
            {' '}
            {formatCurrencyOrMock(
              offer?.mainAmountAdministrationFee,
              '###',
              shouldDisplayLoading,
            )}
          </div>
        </div>
      </div>
      <Spacer marginVertical={'20px'}>
        <Button onClick={onButtonClick} disabled={buttonDisabled}>
          {t.welcomeBackButton}
        </Button>
      </Spacer>
    </Card>
  );
}

export default WelcomeBackCalculator;
